
import { Link } from 'react-router-dom';
import { useState } from 'react';

export default function FuenteIdeasPage() {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = () => {
    setDownloading(true);
    setTimeout(() => {
      setDownloading(false);
      alert('El PDF se descargará próximamente. Estamos preparando el contenido.');
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-[#FAFBF6]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-[#A7A86C]/20 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link 
              to="/mi-espacio" 
              className="flex items-center gap-2 text-gray-600 hover:text-[#A7A86C] transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl"></i>
              <span className="text-sm font-medium">Volver a Mi espacio</span>
            </Link>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-[#A7A86C]/10 rounded-full">
              <i className="ri-gift-line text-[#A7A86C] text-sm"></i>
              <span className="text-xs font-medium text-[#A7A86C]">Ritual Gratuito</span>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-[#A7A86C] via-[#B8B97D] to-[#D6D57B] py-16 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-1/4 w-36 h-36 border border-white/30 rounded-full"></div>
          <div className="absolute bottom-10 right-10 w-24 h-24 border border-white/40 rounded-full"></div>
          <div className="absolute top-1/2 right-1/4 w-20 h-20 bg-white/10 rounded-full"></div>
        </div>
        <div className="max-w-5xl mx-auto px-6 relative">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <i className="ri-lightbulb-flash-line text-3xl text-white"></i>
            </div>
            <div className="flex items-center gap-2 px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full">
              <i className="ri-time-line text-white text-sm"></i>
              <span className="text-sm text-white">18 minutos</span>
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-serif text-white mb-4">
            La fuente de las ideas
          </h1>
          <p className="text-lg text-white/90 max-w-2xl">
            Un ritual para conectar con tus voces inspiradoras y reconocer tu propia voz creativa
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-5 gap-10">
          {/* Left Column - Content */}
          <div className="lg:col-span-3 space-y-10">
            {/* Sobre este ritual */}
            <section>
              <h2 className="text-2xl font-serif text-gray-800 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-[#A7A86C]/20 rounded-lg flex items-center justify-center">
                  <i className="ri-sparkling-line text-[#A7A86C]"></i>
                </span>
                Sobre este ritual
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-600 leading-relaxed mb-4">
                  Las ideas no vienen de la nada. Nacen de un lugar dentro de ti que está conectado con todo lo que has vivido, leído, sentido y soñado. Este ritual te ayuda a encontrar ese lugar.
                </p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Explorarás las voces que te han inspirado a lo largo de tu vida: personas, libros, canciones, momentos. Y descubrirás cómo todas esas influencias se han mezclado para crear algo único: tu propia voz.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Al final de este ritual, tendrás más claridad sobre de dónde vienen tus ideas y cómo puedes acceder a esa fuente cuando la necesites.
                </p>
              </div>
            </section>

            {/* Qué necesitas */}
            <section>
              <h2 className="text-2xl font-serif text-gray-800 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-[#A7A86C]/20 rounded-lg flex items-center justify-center">
                  <i className="ri-checkbox-circle-line text-[#A7A86C]"></i>
                </span>
                Qué necesitas
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: 'ri-time-line', text: '18 minutos de tu tiempo' },
                  { icon: 'ri-pencil-line', text: 'Papel y bolígrafo' },
                  { icon: 'ri-headphone-line', text: 'Silencio o música suave' },
                  { icon: 'ri-focus-3-line', text: 'Mente receptiva' }
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-[#A7A86C]/15">
                    <div className="w-10 h-10 bg-[#A7A86C]/15 rounded-lg flex items-center justify-center">
                      <i className={`${item.icon} text-[#A7A86C] text-lg`}></i>
                    </div>
                    <span className="text-gray-700">{item.text}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Pasos del ritual */}
            <section>
              <h2 className="text-2xl font-serif text-gray-800 mb-6 flex items-center gap-3">
                <span className="w-8 h-8 bg-[#A7A86C]/20 rounded-lg flex items-center justify-center">
                  <i className="ri-route-line text-[#A7A86C]"></i>
                </span>
                Pasos del ritual
              </h2>
              <div className="space-y-6">
                {[
                  {
                    step: 1,
                    title: 'Haz memoria',
                    description: 'Piensa en las personas, libros, películas o momentos que han marcado tu forma de ver el mundo. Anótalos sin filtrar.'
                  },
                  {
                    step: 2,
                    title: 'Encuentra los hilos',
                    description: 'Busca patrones entre tus influencias. ¿Qué tienen en común? ¿Qué te atraía de cada una?'
                  },
                  {
                    step: 3,
                    title: 'Escucha tu voz',
                    description: 'El PDF te guiará a través de preguntas para identificar qué hace única tu perspectiva y tu forma de expresarte.'
                  },
                  {
                    step: 4,
                    title: 'Crea tu mantra',
                    description: 'Al final, escribirás una frase que capture la esencia de tu voz creativa. Algo que puedas recordar cuando necesites inspiración.'
                  }
                ].map((item) => (
                  <div key={item.step} className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#A7A86C] to-[#D6D57B] rounded-xl flex items-center justify-center text-white font-bold text-lg">
                      {item.step}
                    </div>
                    <div className="flex-1 pt-1">
                      <h3 className="font-bold text-gray-800 mb-1">{item.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Right Column - Download Card */}
          <div className="lg:col-span-2">
            <div className="sticky top-24">
              <div className="bg-gradient-to-br from-[#F8F9F3] to-[#D6D57B]/15 rounded-2xl p-6 border border-[#A7A86C]/20 shadow-lg">
                {/* PDF Icon */}
                <div className="text-center mb-6">
                  <div className="w-20 h-20 mx-auto bg-white rounded-2xl shadow-md flex items-center justify-center mb-4">
                    <i className="ri-file-pdf-2-line text-4xl text-[#A7A86C]"></i>
                  </div>
                  <h3 className="text-xl font-serif text-gray-800 mb-1">Guía del Ritual</h3>
                  <p className="text-sm text-gray-500">Descarga tu PDF gratuito</p>
                </div>

                {/* What's included */}
                <div className="space-y-3 mb-6">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Incluye:</p>
                  {[
                    'Mapa de influencias creativas',
                    '6 preguntas de autoexploración',
                    'Ejercicio de conexión de ideas',
                    'Plantilla para tu mantra',
                    'Ritual de cierre'
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <i className="ri-check-line text-[#A8B88A]"></i>
                      <span className="text-sm text-gray-600">{item}</span>
                    </div>
                  ))}
                </div>

                {/* Download Button */}
                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="w-full py-4 bg-gradient-to-r from-[#A7A86C] to-[#D6D57B] text-white rounded-xl font-medium hover:opacity-90 transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap disabled:opacity-70"
                >
                  {downloading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Preparando descarga...
                    </>
                  ) : (
                    <>
                      <i className="ri-download-2-line text-lg"></i>
                      Descargar PDF Gratis
                    </>
                  )}
                </button>

                {/* File info */}
                <p className="text-center text-xs text-gray-400 mt-3">
                  PDF • 2.8 MB • 9 páginas
                </p>
              </div>

              {/* Additional info */}
              <div className="mt-6 p-4 bg-white rounded-xl border border-[#E8E4E0]">
                <div className="flex items-start gap-3">
                  <i className="ri-lightbulb-line text-[#A7A86C] text-lg mt-0.5"></i>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Este ritual es ideal para momentos de bloqueo creativo o cuando sientes que has perdido tu voz.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Other Rituals */}
      <section className="bg-[#F5F2EF] py-12 mt-12">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-serif text-gray-800 mb-6">Otros rituales gratuitos</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Link 
              to="/rituales/musa-interior"
              className="bg-white rounded-xl p-5 border border-[#F7CDCE]/20 hover:shadow-lg transition-all duration-300 flex items-center gap-4 cursor-pointer group"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-[#F7CDCE] to-[#BEA6BE] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <i className="ri-magic-line text-2xl text-white"></i>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-800 mb-1">La Musa que tienes dentro</h3>
                <p className="text-sm text-gray-500">15 min • Conecta con tu inspiración</p>
              </div>
              <i className="ri-arrow-right-line text-gray-400 group-hover:text-[#F7CDCE] transition-colors"></i>
            </Link>
            <Link 
              to="/rituales/planeta-corazon"
              className="bg-white rounded-xl p-5 border border-[#94A5B7]/20 hover:shadow-lg transition-all duration-300 flex items-center gap-4 cursor-pointer group"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-[#94A5B7] to-[#7A8FA5] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <i className="ri-planet-line text-2xl text-white"></i>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-800 mb-1">¿De qué planeta es tu corazón?</h3>
                <p className="text-sm text-gray-500">20 min • Descubre qué te mueve</p>
              </div>
              <i className="ri-arrow-right-line text-gray-400 group-hover:text-[#94A5B7] transition-colors"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F5F2EF] border-t border-[#E8E4E0] py-8">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-sm text-gray-500">
            © 2025 Una voz más que propia. Todos los derechos reservados.
          </p>
          <a
            href="https://readdy.ai/?ref=logo"
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="text-xs text-gray-400 hover:text-[#C4A5C7] transition-colors mt-2 inline-block"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
}
